import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaginationType } from '../../types/utils/pagination.type';
import {
  IssueTrackerListItemType,
  IssueTrackerType,
  UpdateIssueTrackerDto,
} from '../../types/issue-tracker/issue-tracker.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import axios from 'axios';
import SnackbarUtils from '../../utils/SnackbarUtils';
import { ErrorLogListItemType } from '../../types/error-log/error-log.type';
import { AsyncTaskListItemType, AsyncTaskType } from '../../types/async-task/async-task.type';
import { additionalQueryParams } from '../../utils/utilities';
import { NotificationListItemType } from '../../types/notification/notification.type';
import { MasterChangeLogType } from '../../types/master-change-log/master-change-log.type';
import { QueueLogType } from '../../types/log/log.type';
import { XmlRequestLogsType } from '../../types/xml-request-log/xml-request-log.type';
import { ATALogType } from '../../types/ata/ata-log/ata-log.type';
import { GdmsCallbackType } from '../../types/gdms-callback/gdms-callback.type';
import { DialerLogType } from '../../types/dialer/dialer-log/dialer-log.type';
import { UserLogType } from '../../types/user/user-log/user-log.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';
import { AlarmType } from '../../types/alarm/alarm.type';

export const getIssueTrackerList = createAsyncThunk<
  PaginationType<IssueTrackerListItemType>,
  RouteParamsType & { controller: AbortController },
  { state: RootReducerState }
>('logs/getIssueTrackerList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<IssueTrackerListItemType>>(
      `${process.env.REACT_APP_API_URL}/issue-tracker${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching issues!' });
  }
});

export const getIssueTrackerDetails = createAsyncThunk<IssueTrackerType, { id: number }, { state: RootReducerState }>(
  'logs/getIssueTrackerDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<IssueTrackerType>(`${process.env.REACT_APP_API_URL}/issue-tracker/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching issue details!' });
    }
  }
);

export const patchIssueTrackerDetails = createAsyncThunk<
  IssueTrackerType,
  { payload: UpdateIssueTrackerDto; id: number },
  { state: RootReducerState }
>('logs/patchIssueTrackerDetails', async ({ payload, id }, thunkAPI) => {
  try {
    const response = await axios.patch<IssueTrackerType>(
      `${process.env.REACT_APP_API_URL}/issue-tracker/${id}`,
      payload
    );
    SnackbarUtils.success('Issue info has been changed!');
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating issues!' });
  }
});

export const getErrorLogsList = createAsyncThunk<
  PaginationType<ErrorLogListItemType>,
  RouteParamsType & { controller: AbortController },
  { state: RootReducerState }
>('logs/getErrorLogsList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<ErrorLogListItemType>>(
      `${process.env.REACT_APP_API_URL}/error-log${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching error logs!' });
  }
});

export const getAsyncTaskList = createAsyncThunk<
  PaginationType<AsyncTaskListItemType>,
  RouteParamsType & { controller: AbortController },
  { state: RootReducerState }
>('logs/getAsyncTaskList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      null,
      thunkAPI.getState().sharedReducer.includeSuborgs
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<AsyncTaskListItemType>>(
      `${process.env.REACT_APP_API_URL}/async-task${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching async tasks!' });
  }
});

export const getRouterSyncAsyncTaskList = createAsyncThunk<
  {
    in_progress: Array<AsyncTaskType>;
    last_failed: AsyncTaskType;
    last_success: AsyncTaskType;
  },
  { type: string; connectionID: number },
  { state: RootReducerState }
>('logs/getRouterSyncAsyncTaskList', async ({ type, connectionID }, thunkAPI) => {
  try {
    const response = await axios.get<{
      in_progress: Array<AsyncTaskType>;
      last_failed: AsyncTaskType;
      last_success: AsyncTaskType;
    }>(`${process.env.REACT_APP_API_URL}/async-task/latest?type=${type} Sync&connection_id=${connectionID}`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching async tasks!' });
  }
});

export const getRouterUpdateAsyncTaskList = createAsyncThunk<
  {
    in_progress: Array<AsyncTaskType>;
    last_failed: AsyncTaskType;
    last_success: AsyncTaskType;
  },
  { type: string; connectionID: number },
  { state: RootReducerState }
>('logs/getRouterUpdateAsyncTaskList', async ({ type, connectionID }, thunkAPI) => {
  try {
    const response = await axios.get<{
      in_progress: Array<AsyncTaskType>;
      last_failed: AsyncTaskType;
      last_success: AsyncTaskType;
    }>(`${process.env.REACT_APP_API_URL}/async-task/latest?type=${type} Update&connection_id=${connectionID}`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching async tasks!' });
  }
});

export const getRevioReportsList = createAsyncThunk<
  PaginationType<NotificationListItemType>,
  { org_id: number },
  { state: RootReducerState }
>('logs/getRevioReportsList/no-loader', async (params, thunkAPI) => {
  try {
    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<NotificationListItemType>>(
      `${process.env.REACT_APP_API_URL}/tools/revio-report${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching Rev.io Reports!' });
  }
});

export const getAlarmsList = createAsyncThunk<
  PaginationType<AlarmType>,
  { csId: string; limit: number; page: number; order: string },
  { state: RootReducerState }
>('logs/getAlarmsList', async (params, thunkAPI) => {
  try {
    const mergedParams = { ...params };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<AlarmType>>(
      `${process.env.REACT_APP_API_URL}/contact-id/alarms${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching alarms!' });
  }
});

export const getAuditLogsList = createAsyncThunk<
  PaginationType<NotificationListItemType>,
  RouteParamsType & { controller: AbortController },
  { state: RootReducerState }
>('logs/getAuditLogsList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      null,
      thunkAPI.getState().sharedReducer.includeSuborgs
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<NotificationListItemType>>(
      `${process.env.REACT_APP_API_URL}/tools/audit-logs${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching audit logs!' });
  }
});

export const getMasterLogsList = createAsyncThunk<
  PaginationType<MasterChangeLogType>,
  RouteParamsType & { entity_id: number },
  { state: RootReducerState }
>('logs/getMasterLogsList', async (params, thunkAPI) => {
  try {
    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<MasterChangeLogType>>(
      `${process.env.REACT_APP_API_URL}/tools/audit-logs${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching master logs!' });
  }
});

export const getRevIoQueueList = createAsyncThunk<
  PaginationType<QueueLogType>,
  RouteParamsType & { entity_id: number },
  { state: RootReducerState }
>('logs/getRevIoQueueList', async (params, thunkAPI) => {
  try {
    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<QueueLogType>>(
      `${process.env.REACT_APP_API_URL}/tools/revio-queue${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching queue list!' });
  }
});

export const getBoBoRevIoQueueList = createAsyncThunk<
  PaginationType<QueueLogType>,
  RouteParamsType & { entity_id: number },
  { state: RootReducerState }
>('logs/getBoBoRevIoQueueList', async (params, thunkAPI) => {
  try {
    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<QueueLogType>>(
      `${process.env.REACT_APP_API_URL}/tools/revio-queue${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching queue list!' });
  }
});

export const getATATaskLogsList = createAsyncThunk<
  PaginationType<ATALogType>,
  RouteParamsType & { entity_id: number },
  { state: RootReducerState }
>('logs/getATATaskLogsList', async (params, thunkAPI) => {
  try {
    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<ATALogType>>(
      `${process.env.REACT_APP_API_URL}/ata-logs${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting ata action logs!' });
  }
});

export const getGDMSCallbacksList = createAsyncThunk<
  PaginationType<GdmsCallbackType>,
  RouteParamsType & { entity_id: number },
  { state: RootReducerState }
>('logs/getGDMSCallbacksList', async (params, thunkAPI) => {
  try {
    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<GdmsCallbackType>>(
      `${process.env.REACT_APP_API_URL}/atas/statusLogs${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting gdms callback logs!' });
  }
});

export const getXMLRequestList = createAsyncThunk<
  PaginationType<XmlRequestLogsType>,
  RouteParamsType & { entity_id: number },
  { state: RootReducerState }
>('logs/getXMLRequestList', async (params, thunkAPI) => {
  try {
    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<XmlRequestLogsType>>(
      `${process.env.REACT_APP_API_URL}/xml-request-logs${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting xml request logs!' });
  }
});

export const getDialerLogsList = createAsyncThunk<
  PaginationType<DialerLogType>,
  RouteParamsType & { entity_id: number; controller: AbortController },
  { state: RootReducerState }
>('logs/getDialerLogsList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );

    const mergedParams = { ...params, ...additionalParams };

    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<DialerLogType>>(
      `${process.env.REACT_APP_API_URL}/dialer-log${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting dialer logs!' });
  }
});

export const getUserLogsList = createAsyncThunk<
  PaginationType<UserLogType>,
  RouteParamsType & { user_id: number },
  { state: RootReducerState }
>('logs/getUserLogsList', async (params, thunkAPI) => {
  try {
    const mergedParams = { ...params };

    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<UserLogType>>(
      `${process.env.REACT_APP_API_URL}/user-logs${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting user logs!' });
  }
});
